import {strOperations} from '@/composables/globals';

/**
 * The alert taxonomy, as one definition instead of bare ints and string
 * literals spread across the activity feed, the detail panels and the device
 * pages.
 *
 * WHERE THESE NUMBERS COME FROM. They are NOT firmware-originated and do not
 * match anything the device sends. Three unrelated numbering systems exist end
 * to end:
 *
 *   1. firmware/gateway MessageType wire opcodes (PUSH_BUTTON = 0x07,
 *      GEO_FENCE_ALERT = 0x09, LSA = 0x0D, TSA = 0x0E, SPEED_ALERT = 0x13)
 *   2. firmware LocationTriggerSource 0-23 (PUSH_BUTTON_TRIGGER = 8,
 *      LOW_BATTERY = 12, LIGHT_SENSOR_EVENT = 13, BATTERY_REMOVED = 14,
 *      TEMP_ALERT = 17, CRASH_ALERT = 23)
 *   3. the API's AssetAlertTypeEnum 1-13, which is what this mirrors
 *
 * The 1-13 value is minted server-side in ConfigController::monitoringAlert
 * from a bare string the gateway posts, so trigger 8 becomes type 4, trigger 12
 * becomes 7 and trigger 14 becomes 13. Do not try to align these with a
 * firmware constant; they are a different vocabulary.
 *
 * Keep in lockstep with App\Enums\AssetAlertTypeEnum (API), the fleet app's
 * lib/models/alert_type.dart, and the Go web's equivalent composable.
 */

export const ALERT_TYPE = Object.freeze({
    GEOFENCE: 1,
    SPEED: 2,
    LIGHT: 3,
    PUSH_BUTTON: 4,
    TEMPERATURE: 5,
    MAINTENANCE: 6,
    BATTERY: 7,
    CRASH: 8,
    NO_SHOW: 9,
    PLACE: 10,
    DEPARTURE: 11,
    ARRIVAL: 12,
    SOS: 13,
    FUEL_LOW: 14,
});

const ALERT_TYPE_VALUES = new Set(Object.values(ALERT_TYPE));

/**
 * Types that never fire today: 4 (the tracker button now files as 13 with
 * sub_type push_button_sos) and 11/12 (disabled server side). Filters leave
 * them out so nobody picks a type that can only ever return nothing.
 */
export const NEVER_FIRING_ALERT_TYPES = Object.freeze([
    ALERT_TYPE.PUSH_BUTTON,
    ALERT_TYPE.DEPARTURE,
    ALERT_TYPE.ARRIVAL,
]);

/**
 * Short label per type, used by filter chips and type-only places (the unread
 * counts by type). Where a whole notification row is at hand use alertLabel()
 * instead: it reads the sub_type, so a charger event is not called "Battery".
 *
 * The API's `alert_type_name` is a generic bucket ("GENERAL ALERT" for 6/7/8,
 * "SOS ALERT" for 13) kept only for old mobile apps. Never display it.
 */
export const ALERT_TYPE_LABEL = Object.freeze({
    [ALERT_TYPE.GEOFENCE]: 'Geofence',
    [ALERT_TYPE.SPEED]: 'Speed',
    [ALERT_TYPE.LIGHT]: 'Light Sensor',
    [ALERT_TYPE.PUSH_BUTTON]: 'Push Button',
    [ALERT_TYPE.TEMPERATURE]: 'Temperature',
    [ALERT_TYPE.MAINTENANCE]: 'Maintenance / Check Engine',
    [ALERT_TYPE.BATTERY]: 'Battery',
    [ALERT_TYPE.CRASH]: 'Crash',
    [ALERT_TYPE.NO_SHOW]: 'No Show',
    [ALERT_TYPE.PLACE]: 'Place',
    [ALERT_TYPE.DEPARTURE]: 'Departure',
    [ALERT_TYPE.ARRIVAL]: 'Arrival',
    [ALERT_TYPE.SOS]: 'SOS / Tamper',
    [ALERT_TYPE.FUEL_LOW]: 'Low Fuel',
});

/**
 * The `system_alerts` discriminator. The API maps these onto alert types in
 * ConfigController::monitoringAlert:
 *
 *   battery_power_depleted | charger_connected | charger_disconnected -> 7
 *   crash_detention                                                   -> 8
 *   device_unplugged | push_button_sos                                -> 13 (SOS)
 *   maintenance_required                                              -> 6
 *
 * Type 7 carrying three sub_types and type 13 carrying both a panic press and a
 * hardware tamper is why the bare int is not a display taxonomy: rendering on
 * alert_type alone puts an amber "Low Battery" warning on a connected charger.
 */
export const SYSTEM_ALERT_SUB_TYPE = Object.freeze({
    BATTERY_POWER_DEPLETED: 'battery_power_depleted',
    CHARGER_CONNECTED: 'charger_connected',
    CHARGER_DISCONNECTED: 'charger_disconnected',
    CRASH_DETENTION: 'crash_detention',
    DEVICE_UNPLUGGED: 'device_unplugged',
    PUSH_BUTTON_SOS: 'push_button_sos',
    MAINTENANCE_REQUIRED: 'maintenance_required',
});

/** The `type` value the API uses for the whole device-health family. */
export const SYSTEM_ALERTS_TYPE = 'system_alerts';

/** The alert types the backend files under the system_alerts family. */
export const SYSTEM_ALERT_TYPES = Object.freeze([
    ALERT_TYPE.MAINTENANCE,
    ALERT_TYPE.BATTERY,
    ALERT_TYPE.CRASH,
    ALERT_TYPE.SOS,
]);

/** The three sub_types that ride on ALERT_TYPE.BATTERY. */
export const BATTERY_SUB_TYPES = Object.freeze([
    SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED,
    SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED,
    SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED,
]);

/** Human wording per sub_type, so no surface has to spell the wire value. */
export const SYSTEM_ALERT_SUB_TYPE_LABEL = Object.freeze({
    [SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED]: 'Battery Power Depleted',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED]: 'Charger Connected',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED]: 'Charger Disconnected',
    [SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION]: 'Crash Detected',
    [SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED]: 'Tracker Lost Power',
    [SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS]: 'Panic Button Pressed',
    [SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED]: 'Maintenance Required',
});

/**
 * Numeric alert type for a raw row, or null when it carries none / an unknown
 * one. Tolerant by design: a type the server adds later must degrade to the
 * generic rendering, not break the feed. Accepts the string form the API and
 * FCM both send.
 */
export function alertTypeOf(alert) {
    const raw = alert?.alert_type ?? alert?.type;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isFinite(n) && ALERT_TYPE_VALUES.has(n) ? n : null;
}

/**
 * The system_alerts sub_type for a row, or null.
 *
 * Two row shapes reach this and they nest it differently: a REST row from
 * AssetAlertNotificationResource keeps sub_type inside the `payload` map, while
 * a flat push payload carries it at the top level. Read the payload first and
 * fall back to the top level so every surface resolves it identically.
 */
export function subTypeOf(alert) {
    const raw = alert?.payload?.sub_type ?? alert?.sub_type;
    const s = (raw ?? '').toString().trim();
    return s === '' ? null : s;
}

/** True when the backend filed this under the `system_alerts` family. */
export function isSystemAlert(alert) {
    const t = alert?.payload?.type ?? alert?.type;
    if ((t ?? '').toString().trim() === SYSTEM_ALERTS_TYPE) return true;
    if (subTypeOf(alert)) return true;
    return SYSTEM_ALERT_TYPES.includes(alertTypeOf(alert));
}

/** Crash and SOS carry a heightened (red/urgent) accent. */
export function isUrgentType(type) {
    return type === ALERT_TYPE.CRASH || type === ALERT_TYPE.SOS;
}

/** Speeding counts as urgent only this far over the limit, in MPH. */
export const URGENT_SPEED_OVER_MPH = 15;

const numberOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

/**
 * Speed and limit on a speed alert payload, both MPH, either may be null. Older
 * payloads carry {speed, limit}. A limit of 0 means none was set.
 */
function speedAndLimit(alert) {
    const p = alert?.payload || {};
    const limit = numberOrNull(p.speed_threshold ?? p.speed_limit ?? p.limit);
    return {
        speed: numberOrNull(p.current_speed || p.speed),
        limit: limit !== null && limit > 0 ? limit : null,
    };
}

/** True when the alert came from a phone rather than a tracker. */
export function isPhoneAlert(alert) {
    const v = alert?.payload?.is_phone ?? alert?.is_phone;
    return v === true || v === 1 || v === '1' || v === 'true';
}

/** A fuel level as a whole percent, or null when missing or outside 0-100. */
export function fuelPercent(v) {
    const n = numberOrNull(v);
    return n !== null && n >= 0 && n <= 100 ? Math.round(n) : null;
}

/**
 * 'critical' for emergencies, 'high' for alerts that need attention, else null.
 * Same ranking as the Fleet app's severityOf, except speeding is only urgent
 * when it is well over the limit (and not when either number is unknown).
 */
export function alertSeverity(alert) {
    const type = rowTypeOf(alert);
    const sub = subTypeOf(alert);
    // An unplugged tracker is urgent but not an emergency like a panic press or crash.
    if (sub === SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED) return 'high';
    if (type === ALERT_TYPE.CRASH || type === ALERT_TYPE.SOS || sub === SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION) return 'critical';
    // A phone running low on battery is routine, not a fleet problem.
    if (sub === SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED && !isPhoneAlert(alert)) return 'high';
    if (type === ALERT_TYPE.MAINTENANCE || type === ALERT_TYPE.NO_SHOW) return 'high';
    if (type === ALERT_TYPE.SPEED) {
        const {speed, limit} = speedAndLimit(alert);
        if (speed !== null && limit !== null && speed - limit >= URGENT_SPEED_OVER_MPH) return 'high';
    }
    return null;
}

/** Sort rank: 0 emergency, 1 urgent, 2 everything else. */
export function severityRank(alert) {
    const s = alertSeverity(alert);
    return s === 'critical' ? 0 : (s === 'high' ? 1 : 2);
}

/** Display word for a severity, or '' when it has none. */
export function severityLabel(severity) {
    return severity === 'critical' ? 'Emergency' : (severity === 'high' ? 'Urgent' : '');
}

/** True when the alert is a hardware tamper rather than a person's SOS. */
export function isTamper(alert) {
    return subTypeOf(alert) === SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED;
}

/**
 * True when someone pressed the panic button ON THE TRACKER (firmware trigger
 * 8). Files as type 13 like the hardware tamper but is not one: it is a
 * deliberate human action rather than a power event.
 */
export function isPushButton(alert) {
    return subTypeOf(alert) === SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS;
}

/**
 * Display wording for a sub_type, falling back to the title cased wire value so
 * a sub_type this build does not know yet still shows something.
 */
export function subTypeLabel(subType) {
    if (!subType) return '';
    return SYSTEM_ALERT_SUB_TYPE_LABEL[subType]
        || subType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Plain label for a bare type number (no row at hand), or '' when unknown. */
export function alertTypeLabel(type) {
    const n = typeof type === 'number' ? type : parseInt(type, 10);
    return ALERT_TYPE_LABEL[n] || '';
}

/**
 * Specific short label per sub_type, for type columns and list rows. Shorter
 * than SYSTEM_ALERT_SUB_TYPE_LABEL, which is the descriptive heading the detail
 * panels use.
 */
export const ALERT_SUB_TYPE_SHORT_LABEL = Object.freeze({
    [SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED]: 'Low Battery',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED]: 'Charger Connected',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED]: 'Charger Disconnected',
    [SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION]: 'Crash Detected',
    [SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED]: 'Device Unplugged / Tamper',
    [SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS]: 'Panic Button',
    [SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED]: 'Maintenance / Check Engine',
});

/** Numeric type for a row, also looking inside the payload. */
export function rowTypeOf(alert) {
    return alertTypeOf(alert) ?? alertTypeOf(alert?.payload);
}

/**
 * The one label to show for an alert row, keyed on type + sub_type. A phone
 * SOS is type 13 with no sub_type and reads "SOS".
 */
export function alertLabel(alert) {
    const type = rowTypeOf(alert);
    const sub = subTypeOf(alert);
    if (sub && (type === null || SYSTEM_ALERT_TYPES.includes(type))) {
        return ALERT_SUB_TYPE_SHORT_LABEL[sub] || subTypeLabel(sub);
    }
    if (type === ALERT_TYPE.CRASH) return ALERT_SUB_TYPE_SHORT_LABEL[SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION];
    if (type === ALERT_TYPE.MAINTENANCE) return ALERT_SUB_TYPE_SHORT_LABEL[SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED];
    // No sub_type on a 7: it may be a charger event, so do not claim low battery.
    return ALERT_TYPE_LABEL[type] || 'Alert';
}

/** Icon and colour per type. Hex so they read the same in light and dark. */
export const ALERT_TYPE_META = Object.freeze({
    [ALERT_TYPE.GEOFENCE]:    {icon: 'mdi-map-marker-radius',   color: '#FF9800'},
    [ALERT_TYPE.SPEED]:       {icon: 'mdi-speedometer',         color: '#F44336'},
    [ALERT_TYPE.LIGHT]:       {icon: 'mdi-lightbulb-on',        color: '#FFC107'},
    [ALERT_TYPE.PUSH_BUTTON]: {icon: 'mdi-gesture-tap-button',  color: '#9C27B0'},
    [ALERT_TYPE.TEMPERATURE]: {icon: 'mdi-thermometer',         color: '#2196F3'},
    [ALERT_TYPE.MAINTENANCE]: {icon: 'mdi-wrench',              color: '#FF9800'},
    [ALERT_TYPE.BATTERY]:     {icon: 'mdi-battery-alert',       color: '#F44336'},
    [ALERT_TYPE.CRASH]:       {icon: 'mdi-car-emergency',       color: '#F44336'},
    [ALERT_TYPE.NO_SHOW]:     {icon: 'mdi-clock-alert-outline', color: '#795548'},
    [ALERT_TYPE.PLACE]:       {icon: 'mdi-map-marker-check',    color: '#009688'},
    [ALERT_TYPE.DEPARTURE]:   {icon: 'mdi-map-marker-up',       color: '#3F51B5'},
    [ALERT_TYPE.ARRIVAL]:     {icon: 'mdi-map-marker-down',     color: '#3F51B5'},
    [ALERT_TYPE.SOS]:         {icon: 'mdi-alarm-light',         color: '#F44336'},
    [ALERT_TYPE.FUEL_LOW]:    {icon: 'mdi-gas-station',         color: '#F97316'},
});

/** Icon and colour per sub_type; wins over the type when present. */
export const ALERT_SUB_TYPE_META = Object.freeze({
    [SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED]: {icon: 'mdi-battery-alert',  color: '#F44336'},
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED]:      {icon: 'mdi-power-plug',     color: '#4CAF50'},
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED]:   {icon: 'mdi-power-plug-off', color: '#FF9800'},
    [SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION]:        {icon: 'mdi-car-emergency',  color: '#F44336'},
    [SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED]:       {icon: 'mdi-alert-octagon',  color: '#E64A19'},
    [SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS]:        {icon: 'mdi-alarm-light',    color: '#F44336'},
    [SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED]:   {icon: 'mdi-wrench',         color: '#FF9800'},
});

export const DEFAULT_ALERT_ICON = 'mdi-bell-alert';
export const DEFAULT_ALERT_COLOR = '#1976D2';

/** Icon for a bare type number. */
export function alertTypeIcon(type) {
    return ALERT_TYPE_META[parseInt(type, 10)]?.icon || DEFAULT_ALERT_ICON;
}

/** Colour for a bare type number. */
export function alertTypeColor(type) {
    return ALERT_TYPE_META[parseInt(type, 10)]?.color || DEFAULT_ALERT_COLOR;
}

/** Icon for an alert row, sub_type first, then type. */
export function alertIcon(alert) {
    const sub = subTypeOf(alert);
    if (sub && ALERT_SUB_TYPE_META[sub]) return ALERT_SUB_TYPE_META[sub].icon;
    return alertTypeIcon(rowTypeOf(alert));
}

/** Colour for an alert row, sub_type first, then type. */
export function alertColor(alert) {
    const sub = subTypeOf(alert);
    if (sub && ALERT_SUB_TYPE_META[sub]) return ALERT_SUB_TYPE_META[sub].color;
    return alertTypeColor(rowTypeOf(alert));
}

/** {value, label, icon, color} per type for filter lists, never-firing types left out. */
export const ALERT_TYPE_FILTER_OPTIONS = Object.freeze(
    Object.values(ALERT_TYPE)
        .filter((t) => !NEVER_FIRING_ALERT_TYPES.includes(t))
        .map((t) => Object.freeze({
            value: t,
            label: ALERT_TYPE_LABEL[t],
            icon: ALERT_TYPE_META[t].icon,
            color: ALERT_TYPE_META[t].color,
        })),
);

/**
 * Direction of a geofence / place event. enter_or_exit 1 is arrived/entered and
 * ANY other value is left/exited (0 and 2 are both seen). Returns null only when
 * the key is absent, so the direction is never hidden for a 0.
 */
export function enterOrExitOf(payload) {
    const v = payload?.enter_or_exit;
    if (v === null || v === undefined || v === '') return null;
    return Number(v) === 1 ? 'enter' : 'exit';
}

// A whole-number speed in the viewer's unit, e.g. "78 mph".
function speedText(mph) {
    const out = strOperations().unitifyDistance(mph);
    const m = String(out ?? '').match(/^(-?[\d.]+)\s*(\D+)$/);
    return m ? `${Math.round(parseFloat(m[1]))} ${m[2]}` : String(out ?? '');
}

// Fallback wording per type when a row carries nothing more specific.
const TYPE_PHRASE = Object.freeze({
    [ALERT_TYPE.GEOFENCE]: 'geofence alert',
    [ALERT_TYPE.SPEED]: 'was speeding',
    [ALERT_TYPE.LIGHT]: 'light sensor triggered',
    [ALERT_TYPE.PUSH_BUTTON]: 'button pressed',
    [ALERT_TYPE.TEMPERATURE]: 'temperature out of range',
    [ALERT_TYPE.MAINTENANCE]: 'check engine on',
    // A 7 without a sub_type may be a charger event, so it does not claim low battery
    [ALERT_TYPE.BATTERY]: 'battery alert',
    [ALERT_TYPE.CRASH]: 'crash detected',
    [ALERT_TYPE.NO_SHOW]: 'did not arrive',
    [ALERT_TYPE.PLACE]: 'place update',
    [ALERT_TYPE.DEPARTURE]: 'left',
    [ALERT_TYPE.ARRIVAL]: 'arrived',
    [ALERT_TYPE.SOS]: 'SOS emergency',
    [ALERT_TYPE.FUEL_LOW]: 'low on fuel',
});

const SUB_TYPE_PHRASE = Object.freeze({
    [SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED]: 'battery depleted',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED]: 'charger connected',
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED]: 'charger disconnected',
    [SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION]: 'crash detected',
    [SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED]: 'tracker lost power (possible tamper)',
    [SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS]: 'panic button pressed',
    [SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED]: 'check engine on',
});

// [phrase, dash]: verb phrases follow the name with a space, noun phrases with a dash.
function headlineParts(alert, type) {
    const p = alert?.payload || {};
    // template_label is the zone or place; location_name can be an unrelated company location
    const place = p.template_label || alert?.template_label || p.place_name || p.geofence_name || p.location_name || null;
    const dir = enterOrExitOf(p);
    const subType = subTypeOf(alert);
    const sub = subType === SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED && isPhoneAlert(alert)
        ? 'phone battery low'
        : SUB_TYPE_PHRASE[subType];
    switch (type) {
        case ALERT_TYPE.GEOFENCE:
            if (!dir) return place ? [`in ${place}`, false] : [TYPE_PHRASE[type], true];
            return [`${dir === 'enter' ? 'entered' : 'left'} ${place || 'a zone'}`, false];
        case ALERT_TYPE.PLACE:
            if (!dir) return place ? [`at ${place}`, false] : [TYPE_PHRASE[type], true];
            if (dir === 'enter') return [place ? `arrived at ${place}` : 'arrived', false];
            return [place ? `left ${place}` : 'left', false];
        case ALERT_TYPE.ARRIVAL:
            return [place ? `arrived at ${place}` : 'arrived', false];
        case ALERT_TYPE.DEPARTURE:
            return [place ? `left ${place}` : 'left', false];
        case ALERT_TYPE.NO_SHOW:
            return [place ? `did not arrive at ${place}` : 'did not arrive', false];
        case ALERT_TYPE.SPEED: {
            const {speed, limit} = speedAndLimit(alert);
            if (speed !== null && limit !== null) return [`was speeding at ${speedText(speed)} (limit ${speedText(limit)})`, false];
            if (speed !== null) return [`was speeding at ${speedText(speed)}`, false];
            if (limit !== null) return [`was speeding above ${speedText(limit)}`, false];
            return [TYPE_PHRASE[type], false];
        }
        case ALERT_TYPE.TEMPERATURE: {
            const t = numberOrNull(p.current_temperature ?? p.temperature);
            return [t !== null ? `temperature ${strOperations().unitifyTemperature(t)}` : TYPE_PHRASE[type], true];
        }
        case ALERT_TYPE.FUEL_LOW: {
            const f = fuelPercent(p.fuel_level);
            return [f !== null ? `low on fuel (${f}%)` : TYPE_PHRASE[type], true];
        }
        case ALERT_TYPE.BATTERY:
        case ALERT_TYPE.CRASH:
        case ALERT_TYPE.MAINTENANCE:
        case ALERT_TYPE.SOS:
            return [sub || TYPE_PHRASE[type], true];
        default:
            return [TYPE_PHRASE[type], true];
    }
}

/**
 * One sentence saying what happened, e.g. "Honda arrived at Publix" or
 * "Honda - panic button pressed". Wording follows the Fleet app's
 * FleetAlertPhraser.describe(). Null only for a type this build does not know,
 * where the caller shows the stored title instead.
 */
export function alertHeadline(alert) {
    const type = rowTypeOf(alert);
    if (type === null) return null;
    const who = (alert?.asset || alert?.payload?.asset || '').toString().trim() || (isPhoneAlert(alert) ? 'Phone' : 'Vehicle');
    const [phrase, dash] = headlineParts(alert, type);
    return `${who}${dash ? ' - ' : ' '}${phrase}`;
}

const COORDINATE_NUMBER = String.raw`-?\d+(?:\.\d+)?(?:e[-+]?\d+)?`;
const COORDINATES_ONLY = new RegExp(String.raw`^\s*${COORDINATE_NUMBER}\s*,\s*${COORDINATE_NUMBER}\s*$`, 'i');

/**
 * The second line under a headline: how far over the limit a speeding alert
 * was, else the street address, never a bare "lat, lng" pair. Null when there
 * is nothing useful to add.
 */
export function alertDetail(alert) {
    const p = alert?.payload || {};
    if (rowTypeOf(alert) === ALERT_TYPE.SPEED) {
        const {speed, limit} = speedAndLimit(alert);
        if (speed !== null && limit !== null && Math.round(speed - limit) >= 1) return `${speedText(speed - limit)} over the limit`;
    }
    const address = (p.formatted_address || p.location || '').toString().trim();
    return address && !COORDINATES_ONLY.test(address) ? address : null;
}

/**
 * Coordinates on an alert payload. Hardware alerts carry latitude/longitude; a
 * phone SOS carries lat/lng instead. Returns null when either is missing.
 */
export function payloadLatLng(payload) {
    const lat = payload?.latitude ?? payload?.lat;
    const lng = payload?.longitude ?? payload?.lng;
    const missing = (v) => v === null || v === undefined || v === '';
    if (missing(lat) || missing(lng)) return null;
    return {lat, lng};
}

/**
 * Monitoring system alert RULES (SystemAlertTypeEnum in the API). These are rule
 * numbers on a monitoring template, NOT alert numbers: rule 1 is low battery
 * while alert type 1 is a geofence. Do not mix them with ALERT_TYPE.
 */
export const SYSTEM_ALERT_RULE = Object.freeze({
    BATTERY_POWER_DEPLETED: 1,
    CRASH_DETENTION: 2,
    MAINTENANCE_REQUIRED: 3,
    CHARGER_CONNECTED: 4,
    CHARGER_DISCONNECTED: 5,
    DEVICE_UNPLUGGED: 6,
    PUSH_BUTTON_SOS: 7,
});

/** Label per system alert RULE number (see SYSTEM_ALERT_RULE, not alert numbers). */
export const SYSTEM_ALERT_RULE_LABEL = Object.freeze({
    [SYSTEM_ALERT_RULE.BATTERY_POWER_DEPLETED]: 'Low Battery',
    [SYSTEM_ALERT_RULE.CRASH_DETENTION]: 'Crash Detected',
    [SYSTEM_ALERT_RULE.MAINTENANCE_REQUIRED]: 'Check Engine / Fault Codes',
    [SYSTEM_ALERT_RULE.CHARGER_CONNECTED]: 'Charger Connected',
    [SYSTEM_ALERT_RULE.CHARGER_DISCONNECTED]: 'Charger Disconnected',
    [SYSTEM_ALERT_RULE.DEVICE_UNPLUGGED]: 'Device Unplugged / Tamper',
    [SYSTEM_ALERT_RULE.PUSH_BUTTON_SOS]: 'Panic Button',
});

/**
 * Label for a system alert template row from GET monitoring/configs/{id}. The
 * API sends `type_key` (the rule number) and `type` (the enum NAME, such as
 * "PUSH_BUTTON_SOS"); accept either, and fall back to the DB label.
 */
export function systemAlertRuleLabel(template) {
    const byNumber = (v) => {
        const n = typeof v === 'number' ? v : parseInt(v, 10);
        return Number.isFinite(n) ? SYSTEM_ALERT_RULE_LABEL[n] : undefined;
    };
    const byName = (v) => (typeof v === 'string'
        ? SYSTEM_ALERT_RULE_LABEL[SYSTEM_ALERT_RULE[v.trim().toUpperCase()]]
        : undefined);
    return byNumber(template?.type_key) || byName(template?.type) || byNumber(template?.type)
        || template?.label || '';
}
